import { Alert, Checkbox, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { PARTNER_DOCS_URLS } from '../../constants';
import { buildOAuthAuthorizeUrl } from '../../oauth';
import { getOAuthClientIds } from '../../oauth-client-ids';
import type { PartnerModalProps } from '../../types';
import { authoriseAction, COMMON, PartnerModal, saveAction } from '../partner-modal';
import { buildOAuthRedirectUri, generateOAuthState, OAUTH, usePartnerModalSave } from './shared';

const BRIGHTPEARL = 'features:partners.components.brightpearl' satisfies I18nPrefix;

function startOAuthFlow(account: string) {
  const state = generateOAuthState();
  localStorage.setItem(OAUTH.brightpearl.stateKey, state);
  // The account is typed by the merchant, so it has to be encoded before it becomes a path segment.
  window.location.href = buildOAuthAuthorizeUrl(
    `https://oauth.brightpearl.com/authorize/${encodeURIComponent(account)}`,
    {
      response_type: 'code',
      client_id: getOAuthClientIds().brightpearl,
      redirect_uri: buildOAuthRedirectUri(OAUTH.brightpearl.redirectPath),
      state,
    },
  );
}

export function BrightpearlModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);
  // Settings the API has never persisted come back `undefined`, so each one needs a fallback
  // before it seeds a controlled input.
  const [oauthAccount, setOauthAccount] = useState<string>('');
  const [customerTag, setCustomerTag] = useState<string>(
    settings.integrationBrightpearlCustomerTag ?? '',
  );
  const [exemptCodes, setExemptCodes] = useState<string>(
    settings.integrationBrightpearlCustomerExemptCodes ?? '',
  );
  const [sendInvite, setSendInvite] = useState<boolean>(
    settings.integrationBrightpearlSendInvite ?? false,
  );

  const hasOAuthCode = !!settings.integrationBrightpearlOAuthCode;

  function handleSave() {
    const payload: Setting[] = [
      { key: 'integrationBrightpearl', value: true },
      { key: 'integrationBrightpearlCustomerTag', value: customerTag },
      { key: 'integrationBrightpearlCustomerExemptCodes', value: exemptCodes },
      { key: 'integrationBrightpearlSendInvite', value: sendInvite },
    ];
    void save(payload);
  }

  return (
    <PartnerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Brightpearl"
      description={t(`${BRIGHTPEARL}.description`)}
      docsUrl={PARTNER_DOCS_URLS.brightpearl}
      primaryAction={
        hasOAuthCode
          ? saveAction(handleSave, isSaving)
          : authoriseAction('Brightpearl', () => startOAuthFlow(oauthAccount.trim()), {
              disabled: !oauthAccount.trim(),
            })
      }
    >
      {!hasOAuthCode ? (
        <>
          <Text variant="secondary">{t(`${BRIGHTPEARL}.intro`)}</Text>
          <Input
            name="account"
            label={t(`${BRIGHTPEARL}.account_id_label`)}
            value={oauthAccount}
            onChange={setOauthAccount}
          />
        </>
      ) : (
        <>
          <Alert variant="success" headingLevel="h2">
            {t(`${BRIGHTPEARL}.connected_to`, {
              account: settings.integrationBrightpearlAccount,
            })}
          </Alert>
          <Input
            name="integrationBrightpearlCustomerTag"
            label={t(`${BRIGHTPEARL}.customer_tag_label`)}
            value={customerTag}
            onChange={setCustomerTag}
          />
          <Input
            name="integrationBrightpearlCustomerExemptCodes"
            label={t(`${BRIGHTPEARL}.exempt_codes_label`)}
            value={exemptCodes}
            onChange={setExemptCodes}
          />
          <Checkbox
            name="sendInviteEmail"
            label={t(`${COMMON}.send_invite_label`, { partner: 'Brightpearl' })}
            description={t(`${COMMON}.send_invite_description`)}
            checked={sendInvite}
            onChange={() => setSendInvite((prev) => !prev)}
          />
        </>
      )}
    </PartnerModal>
  );
}
